<template>
  <div
    v-if="showOauthProviderManagement"
    :class="$style.oauthProvidersContainer"
  >
    <div
      :class="$style.oauthProvidersList"
    >
      <Button
        :class="$style.oauthProviderActionButton"
        data-test-id="oauthProviderActionButton"
        :isDisabled="disabled"
        :isLoading="isLoading"
        :isMarketingStyle="false"
        :outline="false"
        :text="oauthActionStrings.buttonText"
        type="primary"
        @click="handleOauthAction"
      />
      <div>
        <span>
          {{ oauthActionStrings.buttonLabel }}
        </span>
        <div
          v-if="oauthProviderConnected"
          :class="$style.oauthProviderAccountIdentifier"
        >
          {{ oauthProviderIdentifier }}
        </div>
      </div>
    </div>
    <MessageContainer
      v-if="settingsItem.disableGoogleLogin"
      :class="$style.disabled"
      data-test-id="disableGoogleLoginMessageContainer"
      level="info"
    >
      {{ 'Sign in with Google is disabled for your organization.' }}
    </MessageContainer>
    <MessageContainer
      v-if="linkingError"
      :class="$style.disabled"
      level="error"
    >
      {{ linkingError }}
    </MessageContainer>
  </div>
</template>

<script>
// Language Utilities
import { get } from 'lodash';

// Framework
import { mapActions, mapGetters, mapState } from 'vuex';

// Helpers & Mixins
import * as Constants from 'client/util/Constants';

// Components
import { Button } from '@jumpcloud/ui-components';
import MessageContainer from 'client/components/MessageContainer/MessageContainer';

const {
  ItemTypeConsts,
  StoreModelMap,
  StoreModuleMap,
} = Constants;
const alertModule = 'AlertModule';
const oauthProviderModule = 'OauthProviderManagementModule';
const settingsModel = StoreModelMap[ItemTypeConsts.settings];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const oauthProvider = 'google';

export default {
  name: 'OauthProviderManagement',

  components: {
    Button,
    MessageContainer,
  },

  props: {
    id: {
      type: String,
      default: '',
    },
  },

  computed: {
    ...mapGetters(settingsModule, {
      user: 'getUser',
    }),

    ...mapState(oauthProviderModule, [
      'linkingError',
      'isLoading',
    ]),

    ...mapState(settingsModel, {
      settingsItem: 'item',
    }),

    disabled() {
      return this.isLoading
        || (this.settingsItem.disableGoogleLogin && !this.oauthProviderConnected);
    },

    oauthActionStrings() {
      if (this.oauthProviderConnected) {
        return {
          buttonLabel: 'Disable Login with Google',
          buttonText: 'Disconnect',
        };
      }

      return {
        buttonLabel: 'Enable Login with Google',
        buttonText: 'Connect',
      };
    },

    oauthProviderConnected() {
      return !!get(this.user, 'oauth.google.id');
    },

    oauthProviderIdentifier() {
      return get(this.user, 'oauth.google.email') || get(this.user, 'oauth.google.id');
    },

    showOauthProviderManagement() {
      return this.user._id === this.id;
    },
  },

  methods: {
    ...mapActions(alertModule, [
      'triggerErrorAlert',
      'triggerSuccessAlert',
    ]),

    ...mapActions(oauthProviderModule, [
      'linkOauthProvider',
      'unlinkOauthProvider',
    ]),

    ...mapActions(settingsModule, [
      'fetchUserSettings',
    ]),

    handleOauthAction() {
      if (this.oauthProviderConnected) {
        this.handleOauthUnlinking();
      } else {
        this.handleOauthLinking();
      }
    },

    handleOauthLinking() {
      this.linkOauthProvider(oauthProvider);
    },

    handleOauthUnlinking() {
      this.unlinkOauthProvider(oauthProvider);
    },
  },
};
</script>

<style module>
.disabled {
  font-size: 14px;
  margin: 0;
}

.oauthProviderActionButton {
  flex: 0 0 auto;
  font-size: 14px;
}

.oauthProviderAccountIdentifier {
  font-size: 14px;
}

.oauthProvidersList {
  align-items: center;
  display: flex;
  font-size: 14px;
  gap: 2rem;
  padding: 0;
}

.oauthProvidersContainer {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
</style>
